import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import { CompanyDto, UserProfileDto, RoleDto } from '../types/dtos'; // ✅ Added RoleDto
import { Shield } from 'lucide-react'; // ✅ Added

export default function UserForm() {
    const navigate = useNavigate();
    const { userId } = useParams();
    const isEditMode = !!userId;

    const [companies, setCompanies] = useState<CompanyDto[]>([]);
    const [roles, setRoles] = useState<RoleDto[]>([]); // ✅ Added
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        isActive: true,
        roles: [] as string[], // ✅ Added
        keycloakUserId: '',
        password: '' // ✅ Added for local login
    });

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // 1. Fetch Companies for Dropdown
                const companiesRes = await apiClient.get<CompanyDto[]>('/admin/companies');
                setCompanies(companiesRes.data);

                // 3. Fetch Roles
                const rolesRes = await apiClient.get<RoleDto[]>('/admin/roles');
                setRoles(rolesRes.data);

                // 2. If Edit Mode, Fetch User Details
                if (isEditMode) {
                    const userRes = await apiClient.get<UserProfileDto>(`/admin/users/${userId}`);
                    const user = userRes.data as any; // Cast for role access if DTO not yet updated in all interfaces
                    setFormData({
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        company: user.company || '',
                        isActive: user.isActive,
                        roles: user.roles || [], // Populate roles
                        keycloakUserId: user.keycloakUserId,
                        password: '' // ✅ Added
                    });
                } else {
                    setFormData(prev => ({ ...prev, keycloakUserId: crypto.randomUUID() }));
                }

            } catch (err: any) {
                console.error("Failed to load data", err);
                setError(err.response?.data?.message || "Failed to load data.");
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [isEditMode, userId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (isEditMode) {
                await apiClient.put(`/admin/users/${userId}`, formData);
            } else {
                await apiClient.post('/admin/users', formData);
            }
            navigate('/users');
        } catch (err: any) {
            console.error("Failed to save user", err);
            setError(err.response?.data?.message || err.response?.data || "Failed to save user.");
        } finally {
            setLoading(false);
        }
    };

    if (loadingData) return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;

    return (
        <div className="w-full">
            <div className="max-w-4xl mx-auto pt-6 pb-8">
                    <nav className="mb-6 text-sm text-gray-500">
                        <a href="/users" className="text-blue-600 hover:underline">Users</a> &gt; <span>{isEditMode ? 'Edit User' : 'Create User'}</span>
                    </nav>

                    <div className="bg-white rounded-xl shadow-sm p-8">
                        <h1 className="text-2xl font-bold text-gray-900 mb-6">{isEditMode ? 'Edit User' : 'Create User'}</h1>

                        {error && (
                            <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                                    <input
                                        type="text"
                                        required
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                        value={formData.firstName}
                                        onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                                    <input
                                        type="text"
                                        required
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                        value={formData.lastName}
                                        onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Company</label>
                                <select
                                    required
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                    value={formData.company}
                                    onChange={e => setFormData({ ...formData, company: e.target.value })}
                                >
                                    <option value="">Select a Company</option>
                                    {companies.map(c => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">Assigning a user to a company restricts their access.</p>
                            </div>

                            {!isEditMode && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 font-bold uppercase tracking-wider text-blue-700">Initial Password</label>
                                    <input
                                        type="text" // Using text to fulfill "input text" request, though usually 'password' is safer
                                        required={!isEditMode}
                                        placeholder="Set initial password"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border font-mono"
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    />
                                    <p className="mt-1 text-[10px] text-gray-500 font-medium">Must be communicated securely to the user.</p>
                                </div>
                            )}

                            {/* Keycloak ID is now handled in the background */}

                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-100">
                                <div className="flex items-center mb-4">
                                    <Shield size={20} className="text-blue-600 mr-2" />
                                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Assigned Roles</h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {roles.map(role => (
                                        <label key={role.id} className="flex items-center p-3 bg-white rounded border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                checked={formData.roles.includes(role.roleName)}
                                                onChange={e => {
                                                    const newRoles = e.target.checked
                                                        ? [...formData.roles, role.roleName]
                                                        : formData.roles.filter(r => r !== role.roleName);
                                                    setFormData({ ...formData, roles: newRoles });
                                                }}
                                            />
                                            <div className="ml-3">
                                                <p className="text-sm font-bold text-gray-900">{role.roleName}</p>
                                                <p className="text-[10px] text-gray-500 uppercase font-bold">{role.company || 'Global'}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                {roles.length === 0 && (
                                    <p className="text-sm text-gray-500 italic">No roles available. Please create roles first.</p>
                                )}
                            </div>

                            <div className="flex items-center">
                                <input
                                    id="isActive"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={formData.isActive}
                                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                                    Active Account
                                </label>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => navigate('/users')}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : 'Save User'}
                                </button>
                            </div>
                        </form>
                    </div>
            </div>
        </div>
    );
}
